import React from 'react';
import { useSelector } from 'react-redux';

import { useToggle } from 'Shared/utils/hooks';
import WpButton from 'Wpds/components/button/WpButton';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import { getGoalBySlug, getGoalById } from 'Shared/selectors/Goals.selectors';
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { WpModal, WpModalBody, WpModalFooter, WpModalHeader } from 'Wpds/components/modal';

import ConfirmAddSubGoalSection from './ConfirmAddSubGoalSection/ConfirmAddSubGoalSection';
import { useTranslation } from 'Shared/utils/hooks/use-translation';

type Props = {
  goalId: number;
  chosenGoalSlug: string;
  onToggle(): void;
  onSubmit: Function;
};

function ConfirmAddSubGoalModal({ goalId, chosenGoalSlug, onToggle, onSubmit }: Props) {
  const [isLoading, toggleIsLoading] = useToggle(false);
  const { tr } = useTranslation();

  const handleCancelClick = () => onToggle();

  const handleConfirmClick = () => {
    toggleIsLoading();
    onSubmit();
  };

  const newParentGoal = useSelector(state => getGoalById(state, { id: goalId })?.toJS());

  const chosenGoal = useSelector(state => getGoalBySlug(state, { slug: chosenGoalSlug })?.toJS());

  const chosenGoalParent = useSelector(state => getGoalById(state, { id: chosenGoal.parentId })?.toJS());

  return (
    <WpModal isOpen size="md" onToggle={onToggle} dismissOnBackdrop={false}>
      <WpModalHeader>{tr('perform.goals.confirm_add_sub_goal.header')}</WpModalHeader>
      <WpModalBody>
        {chosenGoalParent && (
          <div className="mb-4">
            <ConfirmAddSubGoalSection
              fromTitle={tr('perform.goals.confirm_add_sub_goal.from_title')}
              toTitle={tr('perform.goals.confirm_add_sub_goal.to_title')}
              fromBody={chosenGoalParent.name}
              toBody={newParentGoal.name}
            >
              <a href={PageUrlsUtils.makeGoalDetailPageUrl(chosenGoal.slug)} target="_blank" rel="noopener noreferrer">
                {chosenGoal.name}
              </a>
              &nbsp;{tr('perform.goals.confirm_add_sub_goal.already_has_parent')}{' '}
              <p>
                <b>{tr('perform.goals.confirm_add_sub_goal.confirm_update_parent')}</b>
              </p>
            </ConfirmAddSubGoalSection>
          </div>
        )}
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={handleCancelClick}>
            {tr('perform.goals.confirm_add_sub_goal.buttons.no_cancel')}
          </WpButton>
          <WpButton qaTest="confirm-change-parent-modal-submit-button" loading={isLoading} onClick={handleConfirmClick}>
            {tr('perform.goals.confirm_add_sub_goal.buttons.yes_update')}
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
}

export default ConfirmAddSubGoalModal;
